/* eslint-disable quotes, array-bracket-newline, react-hooks/exhaustive-deps */
/**
 * The context of the wallet, which is responsible for storing and updating its data
 */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount, useConnect, useDisconnect, useNetwork } from 'wagmi';
import { Network } from '@kattana-io/common';
import { actionLogOut } from '../actions/auth';
import useSignAuthAction from './hooks/useSignAuth';
import useDevice from '../pages/MainPage/useMobileDetect';
import { IStore } from '../reducers';
import { getConnector } from './helpers';
import walletReducer, {
  defaultWalletState,
  IWalletState,
} from './walletReducer';
import useLoadWalletData from './hooks/useLoadWalletData';
import { subscription } from '../store/createStore';
import { actionSetNetwork } from '../actions/network';
import { previouslyLoggedIn } from '../helpers/auth';
import { actionCreateNotification } from '../actions/notifications';
import { actionShowModal } from '../actions/modal';
import { ModalTypes } from '../reducers/modal';

interface IWalletProviderProps {
  children: ReactNode;
}

type OnConnect = (connector?: any, onCancel?: () => void) => Promise<void>;
type onDisconnect = () => void;
export type OnRefresh = (addresses: string[]) => void;

const WalletStateContext = createContext<
  | {
      onConnect: OnConnect;
      onDisconnect: onDisconnect;
      onRefresh: OnRefresh;
      state: IWalletState;
      isConnecting: boolean;
      isSignLoading: boolean;
      isConnected: boolean;
      isError: boolean;
    }
  | undefined
>(undefined);

const WalletProvider = ({ children }: IWalletProviderProps) => {
  const [state, dispatch] = useReducer(walletReducer, defaultWalletState);
  const appDispatch = useDispatch();
  const auth = useSelector((state: IStore) => state.auth);
  const { chain } = useNetwork();
  const { address, isConnected, isConnecting } = useAccount();
  const { isMobile } = useDevice();
  const { connectAsync, connect, isError, connectors, reset } = useConnect();
  const { disconnect } = useDisconnect();
  const {
    chechAuth,
    onSignAuth,
    isLoading: isSignLoading,
  } = useSignAuthAction();

  const activeConnector = useMemo(() => getConnector(connectors), [connectors]);

  /**
   * Connect wallet on app mount
   */
  useEffect(() => {
    if (localStorage) {
      const isPreviouslyLoggedIn = previouslyLoggedIn();

      if (isPreviouslyLoggedIn) {
        connect({ connector: activeConnector });
      }
    }
  }, [connect, activeConnector]);

  /**
   * Disconnect if authorization failed
   */
  useEffect(() => {
    if (auth.authFailed) {
      disconnect();
    }
  }, [auth.authFailed, disconnect]);

  /**
   * Login via evm, elrond or tron wallets
   */
  useEffect(() => {
    try {
      // Do not use evm notation for TRON and ELROND networks
      const internalNetwork = chain?.network;
      let network = 'EVM';
      if (internalNetwork == 'ELROND') {
        network = 'ELROND';
      } else if (internalNetwork == 'TRON') {
        network = 'TRON';
      } else if (internalNetwork == 'TON') {
        network = 'TON';
      }

      // Try to auth user when wallet connected, if sign is rejected or user
      // disconnects by wallet native functionality, trigger client logout
      if (address) {
        const isAuthenticated = chechAuth(network, address);
        if (!isAuthenticated) {
          const onAuth = () =>
            onSignAuth(network, address, window.navigator.userAgent);
          appDispatch(
            actionShowModal(ModalTypes.sign, { onAuth, onDisconnect })
          );
        }
      } else if (auth.loggedIn) {
        appDispatch(actionLogOut());
        subscription.unsubscribeFromPriceAlerts();
        subscription.unsubscribeFromLimitOrders();
      }
    } catch (e) {
      console.log({ e });
      appDispatch(actionLogOut());
      subscription.unsubscribeFromPriceAlerts();
      subscription.unsubscribeFromLimitOrders();
    }
  }, [address]);

  useEffect(() => {
    if (chain) {
      const network = Network.fromChainId(chain.id);
      appDispatch(actionSetNetwork(network));
    }
  }, [chain]);

  /**
   * Update wallet state with account and network changes
   */
  const { onRefresh } = useLoadWalletData({
    account: address,
    chainId: chain?.id,
    noprovider: isMobile,
    dispatch,
    state,
  });

  /**
   * Connect wallet
   */
  const onConnect = useCallback<OnConnect>(
    async (connector, onCancel) => {
      onCancel?.();
      try {
        reset();
        // if there is another connecter we connect using that one
        // in other cases use the initial one (activeConnector)
        if (connector) {
          await connectAsync({ connector });
        } else {
          await connectAsync({ connector: activeConnector });
        }
      } catch (error: any) {
        if ((error.message as string).includes('Connector already connected')) {
          appDispatch(
            actionCreateNotification({
              kind: 'warning',
              message: 'Wallet already connected',
            })
          );
        } else {
          appDispatch(
            actionCreateNotification({
              kind: 'warning',
              message:
                "Please check your wallet, it looks like it's locked or unauthorized",
            })
          );
        }
      }
    },
    [activeConnector, appDispatch, connectAsync, reset]
  );

  /**
   * Disconnect and logout from wallet
   */
  const onDisconnect = useCallback(() => {
    disconnect();
  }, [disconnect]);

  const value = useMemo(() => {
    return {
      onConnect,
      onDisconnect,
      onRefresh,
      state,
      isConnecting,
      isSignLoading,
      isConnected,
      isError,
    };
  }, [
    onConnect,
    onDisconnect,
    onRefresh,
    state,
    isConnected,
    isConnecting,
    isError,
    isSignLoading,
  ]);

  return (
    <WalletStateContext.Provider value={value}>
      {children}
    </WalletStateContext.Provider>
  );
};

const useWallet = () => {
  const context = useContext(WalletStateContext);
  if (context === undefined) {
    throw new Error('useCount must be used within a CountProvider');
  }
  return context;
};

export { WalletProvider, useWallet };
